import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Box,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  Grow,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { submitTokenDeletion, verifyTokenDeletion } from '../../../api/apiCall';
import { useParams } from 'react-router-dom';

const DeletionRequestPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [dialogError, setDialogError] = useState('');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const [verifyingToken, setVerifyingToken] = useState(true);
  const [verificationError, setVerificationError] = useState<string | null>(null);

  const reasonsList = [
    { value: 'privacy_concerns', label: 'Privacy concerns' },
    { value: 'found_alternative', label: 'Found a better alternative' },
    { value: 'not_relevant', label: 'Not relevant to me' },
    { value: 'other', label: 'Other' },
    { value: 'no_reason', label: "I'd rather not say" },
  ];

  const resetReasonStates = () => {
    setSelectedReason('');
    setCustomReason('');
    setDialogError('');
  };

  const handleOpenDialog = () => {
    resetReasonStates();
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    resetReasonStates();
  };

  const requestDeletion = async (reason: string) => {
    const response = await submitTokenDeletion(token, reason);
  };

  const handleConfirm = async () => {
    setLoading(true);
    setDialogError('');

    let finalReason = '';
    if (selectedReason === 'other') {
      finalReason = customReason.trim() || 'Other (no details provided)';
    } else if (selectedReason && selectedReason !== 'no_reason') {
      const reasonObj = reasonsList.find(r => r.value === selectedReason);
      finalReason = reasonObj ? reasonObj.label : selectedReason;
    } else if (selectedReason === 'no_reason') {
      finalReason = 'No reason provided';
    }

    try {
      await requestDeletion(finalReason);
      setDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'Your deletion request has been submitted successfully.',
        severity: 'success',
      });
      resetReasonStates();
    } catch (err: any) {
      setDialogError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerificationError('No deletion token provided.');
        setVerifyingToken(false);
        return;
      }

      try {
        const response = await verifyTokenDeletion(token);
        if (response.success) {
          setVerificationError(null);
        } else {
          setVerificationError(response.message || 'Invalid or expired deletion token.');
        }
      } catch (err: any) {
        setVerificationError(err.message || 'Invalid or expired deletion token.');
      } finally {
        setVerifyingToken(false);
      }
    };

    verifyToken();
  }, [token]);

  if (verifyingToken) {
    return (
      <Container maxWidth="sm" style={{ marginTop: -100 }}>
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (verificationError) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Grow in>
          <Paper elevation={3} sx={{ p: 5, textAlign: 'center', borderRadius: 4 }}>
            <Typography variant="h5" gutterBottom color="error">
              Link Error
            </Typography>
            <Typography variant="body1" color="textSecondary" component="p" sx={{ mb: 2 }}>
              {verificationError}
            </Typography>
          </Paper>
        </Grow>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" style={{ marginTop: -100 }}>
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Request Account Deletion
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 3
            }}>
            Permanently delete your account and all associated data from PACE. This action cannot be undone.
          </Typography>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleOpenDialog}
            disabled={loading}
          >
            Request Deletion
          </Button>
        </Paper>
      </Box>

      <Dialog
        maxWidth="sm"
        fullWidth
        open={dialogOpen}
        onClose={(_event, reason) => {
          if (loading && reason === "escapeKeyDown") return;
          handleCloseDialog();
        }}
      >
        <DialogTitle>Confirm Account Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to delete your account? All your data will be permanently removed from PACE Infrastructure Solutions LTD.
          </DialogContentText>

          <FormControl component="fieldset" sx={{ mt: 1, width: '100%' }}>
            <FormLabel component="legend">Reason for leaving (optional)</FormLabel>
            <RadioGroup
              value={selectedReason}
              onChange={(e) => setSelectedReason(e.target.value)}
              sx={{ mt: 1 }}
            >
              {reasonsList.map((reason) => (
                <FormControlLabel
                  key={reason.value}
                  value={reason.value}
                  control={<Radio />}
                  label={reason.label}
                  disabled={loading}
                />
              ))}
            </RadioGroup>
          </FormControl>

          {selectedReason === 'other' && (
            <TextField
              label="Please specify"
              multiline
              rows={3}
              fullWidth
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              placeholder="Tell us why you're leaving..."
              disabled={loading}
              sx={{ mt: 2 }}
            />
          )}

          {dialogError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {dialogError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            color="error"
            disabled={loading || (selectedReason === 'other' && !customReason.trim())}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Processing...' : 'Yes, Delete My Account'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DeletionRequestPage;
